<template>
  <a-drawer
    placement="right"
    :closable="true"
    :width="1080"
    @close="$emit('close')"
    :visible="isOpened"
  >
    <template v-if="payload">
      <div
        class="custom-title"
        slot="title"
      >
        {{ $t("synchronization.sync_approve_title") }}

        <span
          class="custom-close-button"
          @click="$emit('close')"
        >
          <a-icon type="close"></a-icon>
        </span>
      </div>

      <div class="window__subtitle">
        {{ $t("synchronization.window_description") }}
      </div>

      <div class="window__product">
        <div class="window__product-thumbnail">
          <Thumbnail :images="payload.images ? payload.images : []" />
        </div>
        <div class="window__product-description">
          <div class="window__product-row">
            {{ $t("synchronization.xml_code") }}: {{ payload.xml_id }}
          </div>
          <div class="window__product-row">
            {{ payload.name }}
          </div>
        </div>
        <div class="window__product-params">
          <div class="window__product-row">
            {{ $tc("synchronization.quantity_count", quantity) }}
          </div>
          <div class="window__product-row">
            {{ $t("synchronization.price", { price: getPriceWithCurrency(payload.price, true) }) }}
          </div>
        </div>
      </div>

      <div class="window__subtitle">
        {{ $t("synchronization.list_of_existing_offers") }}
        <ProductCode
          v-if="pCode"
          :code="pCode"
          :slug="slug"
        />
        {{ $t("synchronization.with_warranty") }}
        {{ $tc("synchronization.warranty_in_month", payload.warranty) }}
        {{ $t("synchronization.and_delivery") }}
        {{ `"${payload.delivery ? payload.delivery.name : ""}"` }}
      </div>

      <a-table
        v-if="isOpened"
        @change="handleTableChange"
        :loading="fetching"
        :columns="columns"
        :dataSource="offers"
        rowKey="uuid"
        size="large"
        :pagination="false"
      >
        <div slot="warrantyTitle">
          {{ $t("synchronization.offers_titles.state") }}
        </div>
        <div
          slot="code"
          slot-scope="code, record"
          :class="record.is_synced ? 'window__disabled' : ''"
        >
          {{ record.code }}
        </div>
        <div
          slot="quantity"
          slot-scope="quantity, record"
          :class="record.is_synced ? 'window__disabled' : ''"
        >
          {{ record.quantity }}
        </div>
        <div
          slot="warrantyRenderer"
          slot-scope="warranty, record"
          :class="record.is_synced ? 'window__disabled' : ''"
        >
          {{ parseStateDescription(record.condition_key, record.condition_value) }}
        </div>
        <div
          slot="price"
          slot-scope="price, record"
          :class="record.is_synced ? 'window__disabled' : ''"
        >
          {{ getFormattedPrice(price) }}
        </div>
        <div
          class="window__status"
          slot="state"
          slot-scope="text, record"
        >
          <span
            :class="record.is_sells ? 'window__status_for-sale' : 'window__status_not_for-sale'"
          >
            {{ record.is_sells ? $t("offers.for_sale") : $t("offers.not_for_sale") }}
          </span>
        </div>
        <div
          slot="actions"
          class="window__actions"
          slot-scope="data, record"
        >
          <a-button
            v-if="!record.is_synced"
            @click="handleConnect(record)"
            :loading="currentFetching === `connecting-${record.uuid}`"
            :disabled="Boolean(currentFetching) && currentFetching !== `connecting-${record.uuid}`"
            size="large"
            type="primary"
            ghost
          >
            {{ $t(`synchronization.connect`) }}
          </a-button>
          <div
            class="window__xml"
            v-else
          >
            {{ $t("synchronization.connected_with_xml") }}
            <br />
            ID: {{ record.external_uid }}
          </div>
        </div>
      </a-table>

      <div class="footer">
        <a-button
          :style="{ marginRight: '8px' }"
          size="large"
          @click="$emit('close')"
        >
          {{ $t("common.cancel") }}
        </a-button>
        <a-button
          @click="createNewOffer"
          size="large"
          type="primary"
          :loading="currentFetching === 'createNewOffer'"
          :disabled="Boolean(currentFetching) && currentFetching !== 'createNewOffer'"
        >
          {{ $t(`synchronization.create_new_offer`) }}
        </a-button>
      </div>
    </template>
  </a-drawer>
</template>

<script lang="ts" setup>
import { message } from "ant-design-vue"
import { storeToRefs } from "pinia"
import { computed, ref, watch } from "vue"

import ProductCode from "@/components/Common/ProductCode"
import Thumbnail from "@/components/Common/Thumbnail"
import i18n from "@/i18n"
import { useSyncDraftsStore } from "@/modules/sync/store/useSyncDraftsStore"
import { useSyncOffersBindingStore } from "@/modules/sync/store/useSyncOffersBindingStore"
import { OfferListItem } from "@/types/offers.type"
import { SyncDraft } from "@/types/sync.type"
import getFormattedPrice from "@/utils/getFormattedPrice"
import getPriceWithCurrency from "@/utils/getPriceWithCurrency"
import getProductState from "@/utils/getProductState"
import makeQuery from "@/utils/makeQuery"

import { useApproveWindowColumns } from "./useApproveWindowColumns"

// Fix: Define dummy variables to hint types for Volar in legacy slot usage
// This is required because Volar cannot infer slot-scope types in usage of AntDV 1.x with script setup
const record = {} as OfferListItem
// endFix

// Helper Types/Variables
type Props = {
  isOpened: boolean
  syncUuid: string
  payload: SyncDraft | null
}

const props = defineProps<Props>()
const emit = defineEmits(["close", "approved"])
// End Helper Types/Variables

// Store & Route
const syncOffersBindingStore = useSyncOffersBindingStore()
const syncDraftsStore = useSyncDraftsStore()

const { offers, fetching } = storeToRefs(syncOffersBindingStore)
// End Store & Route

// State
const currentFetching = ref("")
// End State

// Composables
const { columns } = useApproveWindowColumns()
// End Composables

// Computed
const pCode = computed(() => props.payload?.product?.p_code || "")
const slug = computed(() => props.payload?.product?.slug || "")
const price = computed(() => props.payload?.price || "")
const quantity = computed(() => props.payload?.quantity || 0)
const query = computed(() => {
  const result = {}

  if (Object.keys(props.payload).length) {
    result["condition_key"] = props.payload.condition_key

    // push 'condition_value' to query only when condition_key === used
    if (props.payload.condition_key === "used") {
      result["condition_value"] = props.payload.condition_value
    }

    result["delivery"] = props.payload.delivery.uuid
    result["warranty"] = props.payload.warranty
    result["product"] = props.payload.product.p_code
    result["ordering"] = "is_synced"
  }

  return result
})
// End Computed

// Methods
const parseStateDescription = (key, value) => {
  return getProductState(key, value)?.description
}
const handleTableChange = (pagination, filters, sorter) => {
  const queryParams = makeQuery({ filters, sorter })

  syncOffersBindingStore.getOffers({
    query: Object.assign({}, query.value, queryParams)
  })
}
const createNewOffer = () => {
  currentFetching.value = "createNewOffer"

  syncDraftsStore
    .updateDraft({
      payload: {
        _approve: true
      },
      synchronizationUuid: props.syncUuid,
      draftUuid: props.payload.uuid
    })
    .then((approvedDraft) => {
      message.success(i18n.t("synchronization.succesful_created") as string)
      emit("approved", approvedDraft)
    })
    .finally(() => {
      stopFetching()
    })
}
const handleConnect = (record) => {
  currentFetching.value = `connecting-${record.uuid}`

  syncDraftsStore
    .updateDraft({
      payload: {
        _approve: true,
        price: record.price,
        delivery: record.delivery.uuid,
        offer: record.uuid,
        warranty: record.warranty
      },
      synchronizationUuid: props.syncUuid,
      draftUuid: props.payload.uuid
    })
    .then((approvedDraft) => {
      message.success(i18n.t("synchronization.succesful_connected") as string)
      emit("approved", approvedDraft)
    })
    .finally(() => {
      stopFetching()
    })
}
const stopFetching = () => {
  currentFetching.value = ""
}
// End Methods

// Lifecycle
watch(
  () => props.isOpened,
  (bool) => {
    if (!bool) return

    syncOffersBindingStore.clearState()
    syncOffersBindingStore
      .getOffers({
        query: query.value
      })
      .then((results) => {
        if (results.count === 0) {
          createNewOffer()
        }
      })
  }
)
// End Lifecycle
</script>

<style lang="scss" scoped>
.footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
}

.window {
  &__actions {
    display: flex;
    justify-content: flex-end;
  }

  &__subtitle {
    color: rgba(0, 0, 0, 0.85);
    margin-bottom: 12px;
  }

  &__product {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;

    &-thumbnail {
      width: 90px;
    }

    &-description {
      width: 100%;
      padding: 0 24px;
    }

    &-params {
      width: 400px;
    }

    &-row {
      margin-bottom: 6px;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  &__xml {
    width: 100%;
  }

  &__disabled {
    color: rgba(0, 0, 0, 0.25);
  }

  &__status {
    white-space: nowrap;
    display: inline-flex;
    align-items: center;

    &_for-sale {
      color: rgb(82, 196, 26);
    }

    &_not_for-sale {
      color: #f5222d;
    }
  }
}
</style>
